import React, { useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import Logo from '@/components/site/Logo';

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to={loc.state?.from || '/admin'} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success('Welcome back, Shally ✨');
      nav(loc.state?.from || '/admin', { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, 'Invalid credentials'));
    } finally {
      setSubmitting(false);
    }
  };

  const input = 'w-full bg-transparent border-b border-brandborder focus:border-maroon outline-none py-3 text-charcoal placeholder-charcoal/40 transition-colors';

  return (
    <main className="min-h-screen bg-ivory text-charcoal flex items-center justify-center px-6 py-20 mandala-bg">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md bg-surface border border-brandborder shadow-card p-10"
        data-testid="admin-login-card"
      >
        <div className="flex items-center gap-3 mb-8">
          <Logo size="lg" />
          <div>
            <div className="overline">Trendy Closet</div>
            <div className="font-display text-xl">Management Dashboard</div>
          </div>
        </div>
        <h1 className="font-display text-3xl text-charcoal leading-tight">
          Welcome <em className="text-maroon not-italic font-medium">back</em>.
        </h1>
        <p className="text-sm text-charcoal/65 mt-2">Sign in to manage your collection.</p>

        <form onSubmit={submit} className="mt-8 space-y-6" data-testid="admin-login-form">
          <div>
            <label className="overline">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email"
              required
              autoFocus
              className={input}
              placeholder="chathleyshally@gmail.com"
            />
          </div>
          <div>
            <label className="overline">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password"
              required
              className={input}
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-burgundy hover:bg-maroon text-ivory text-xs uppercase tracking-[0.22em] transition-colors duration-500 disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </motion.div>
    </main>
  );
}
